// src/contexts/LocationContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { formatLocationForFirestore } from '../utils/geoUtils';
import { parseAddressComponents } from '../utils/googlePlaces';

const LocationContext = createContext(null);

export const LocationProvider = ({ children }) => {
  const defaultLocation = {
    city: 'Hyderabad',
    state: 'Telangana',
    street: '',
    village: 'Gachibowli',
    neighbourhood: 'Gachibowli',
    latitude: 17.4401,
    longitude: 78.3489,
    display: 'Gachibowli, Hyderabad'
  };

  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);

  const reverseGeocode = (lat, lng) => {
    return new Promise((resolve, reject) => {
      if (!window.google) {
        reject(new Error('Google Maps not loaded'));
        return;
      }

      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results?.[0]) {
          const address = parseAddressComponents(results[0].address_components);

          const formatted = formatLocationForFirestore(address, lat, lng);
          formatted.display = results[0].formatted_address;
          resolve(formatted);
        } else {
          reject(new Error('Geocoding failed'));
        }
      });
    });
  };

  const updateSiteLocation = async (newLoc) => {
    setLocation(newLoc);
    localStorage.setItem('qinto_user_location', JSON.stringify(newLoc));

    if (auth.currentUser) {
      try {
        await updateDoc(doc(db, 'users', auth.currentUser.uid), {
          address: newLoc,
        });
      } catch (e) {
        console.error('Failed to sync location to profile', e);
      }
    }
  };

  const detectBrowserLocation = () => {
    if (!navigator.geolocation) {
      setLocation((prev) => prev || defaultLocation);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const detectedLoc = await reverseGeocode(latitude, longitude);
          await updateSiteLocation(detectedLoc);
        } catch (error) {
          console.error('Auto-detect failed:', error);
          setLocation((prev) => prev || defaultLocation);
        } finally {
          setLoading(false);
        }
      },
      (error) => {
        console.error('Location permission denied or unavailable:', error);
        setLocation((prev) => prev || defaultLocation);
        setLoading(false);
      }
    );
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists() && userDoc.data().locationData) {
            const userLocation = userDoc.data().locationData;
            setLocation(userLocation);
            localStorage.setItem('qinto_user_location', JSON.stringify(userLocation));
            setLoading(false);
            return;
          }
        }

        const saved = localStorage.getItem('qinto_user_location');
        if (saved) {
          setLocation(JSON.parse(saved));
          setLoading(false);
          return;
        }

        detectBrowserLocation();
      } catch (error) {
        console.error('Failed to initialize location context:', error);
        setLocation(defaultLocation);
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  return (
    <LocationContext.Provider value={{ location, updateSiteLocation, loading, detectBrowserLocation, reverseGeocode }}>
      {!loading && children}
    </LocationContext.Provider>
  );
};

export const useLocation = () => useContext(LocationContext);
